import React from 'react';
import type { IntellioAssistantConversationSessionRecord } from '@triedge/domain-core';

const formatSessionTime = (value: number | null | undefined): string => {
  if (!Number.isFinite(value)) {
    return 'Just now';
  }
  const date = new Date(Number(value) * 1000);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

type AssistantConversationSessionLeadProps = {
  currentSession: IntellioAssistantConversationSessionRecord | null;
  previousSession: IntellioAssistantConversationSessionRecord | null;
  onShowFullTimeline: () => void;
  onStartNewSession: () => void;
  onOpenPreviousSession?: ((sessionId: string) => void) | null;
};

export const AssistantConversationSessionLead: React.FC<AssistantConversationSessionLeadProps> = ({
  currentSession,
  previousSession,
  onShowFullTimeline,
  onStartNewSession,
  onOpenPreviousSession,
}) => (
  <div className="space-y-3">
    {previousSession ? (
      <section
        className="rounded-[24px] border border-slate-200/80 bg-white/90 px-4 py-4 shadow-[0_16px_36px_rgba(36,50,53,0.05)]"
        data-testid="ai-previous-session-summary"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-irongrey/55">Previous session</p>
            <h3 className="mt-1 truncate text-sm font-semibold text-slate-900">
              {previousSession.title ?? previousSession.routeLabel ?? previousSession.route}
            </h3>
            <p className="mt-2 text-sm leading-6 text-irongrey/75">
              {previousSession.lastAssistantPreview
                ?? previousSession.lastMessagePreview
                ?? previousSession.firstUserPreview
                ?? 'Earlier assistant activity is available in Threads.'}
            </p>
            <p className="mt-2 text-[12px] text-irongrey/58">
              {previousSession.messageCount} message{previousSession.messageCount === 1 ? '' : 's'} · {formatSessionTime(previousSession.updatedAt)}
            </p>
          </div>
          {onOpenPreviousSession ? (
            <button
              type="button"
              className="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-irongrey transition-colors hover:border-primary/20 hover:bg-primary/5 hover:text-primary"
              onClick={() => onOpenPreviousSession(previousSession.id)}
              data-testid="ai-previous-session-open"
            >
              Reopen
            </button>
          ) : null}
        </div>
      </section>
    ) : null}

    <section
      className="rounded-[24px] border border-primary/15 bg-[linear-gradient(135deg,rgba(38,151,162,0.08),rgba(255,255,255,0.98))] px-4 py-4 shadow-[0_18px_38px_rgba(36,50,53,0.06)]"
      data-testid="ai-session-block-current"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">Current session</p>
          <h3 className="mt-1 truncate text-sm font-semibold text-slate-900">
            {currentSession?.title ?? currentSession?.routeLabel ?? currentSession?.route ?? 'Route-grounded session'}
          </h3>
          <p className="mt-2 text-sm leading-6 text-irongrey/75">
            {currentSession?.messageCount
              ? `${currentSession.messageCount} message${currentSession.messageCount === 1 ? '' : 's'} in this session.`
              : 'This session starts clean and keeps only the current route, module, and persona grounding.'}
          </p>
          {currentSession?.updatedAt ? (
            <p className="mt-2 text-[12px] text-irongrey/58">
              Updated {formatSessionTime(currentSession.updatedAt)}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-full border border-primary/15 bg-white/90 px-3 py-1.5 text-[12px] font-semibold text-primary transition-colors hover:bg-white"
            onClick={onShowFullTimeline}
            data-testid="ai-session-full-timeline-toggle"
          >
            Show full timeline
          </button>
          <button
            type="button"
            className="rounded-full bg-primary px-3 py-1.5 text-[12px] font-semibold text-white transition-opacity hover:opacity-90"
            onClick={onStartNewSession}
            data-testid="ai-session-new-session-control"
          >
            New session
          </button>
        </div>
      </div>
    </section>
  </div>
);
